import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PartyManagementForm = ({ formData, onChange, errors }) => {
  const [expandedParty, setExpandedParty] = useState(null);

  const partyRoles = [
    { value: 'client', label: 'Client' },
    { value: 'vendor', label: 'Vendor/Service Provider' },
    { value: 'contractor', label: 'Contractor' },
    { value: 'consultant', label: 'Consultant' },
    { value: 'partner', label: 'Partner' },
    { value: 'licensor', label: 'Licensor' },
    { value: 'licensee', label: 'Licensee' },
    { value: 'other', label: 'Other' }
  ];

  const entityTypes = [
    { value: 'corporation', label: 'Corporation' },
    { value: 'llc', label: 'Limited Liability Company (LLC)' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'individual', label: 'Individual' },
    { value: 'government', label: 'Government Entity' },
    { value: 'nonprofit', label: 'Non-Profit Organization' }
  ];

  const parties = formData?.parties || [];

  const addParty = () => {
    const newParty = {
      id: Date.now()?.toString(),
      role: '',
      entityType: 'corporation',
      companyName: '',
      contactName: '',
      title: '',
      email: '',
      phone: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'United States'
      },
      signatureRequired: true
    };

    const updatedParties = [...parties, newParty];
    onChange('parties', updatedParties);
    setExpandedParty(newParty?.id);
  };

  const removeParty = (partyId) => {
    const updatedParties = parties?.filter(party => party?.id !== partyId);
    onChange('parties', updatedParties);
    if (expandedParty === partyId) {
      setExpandedParty(null);
    }
  };

  const updateParty = (partyId, field, value) => {
    const updatedParties = parties?.map(party => {
      if (party?.id === partyId) {
        if (field?.includes('.')) {
          const [parent, child] = field?.split('.');
          return {
            ...party,
            [parent]: {
              ...party?.[parent],
              [child]: value
            }
          };
        }
        return { ...party, [field]: value };
      }
      return party;
    });
    onChange('parties', updatedParties);
  };

  const togglePartyExpansion = (partyId) => {
    setExpandedParty(expandedParty === partyId ? null : partyId);
  };

  const getPartyDisplayName = (party) => {
    if (party?.companyName) return party?.companyName;
    if (party?.contactName) return party?.contactName;
    return `${party?.role || 'New Party'}`;
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <span className="text-primary font-semibold text-sm">2</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Party Management</h3>
            <p className="text-sm text-muted-foreground">Add and configure contract parties</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={addParty}
          iconName="Plus"
          iconPosition="left"
        >
          Add Party
        </Button>
      </div>
      {parties?.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
          <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h4 className="text-lg font-medium text-foreground mb-2">No Parties Added</h4>
          <p className="text-muted-foreground mb-4">Add parties to define who will be involved in this contract</p>
          <Button
            variant="default"
            onClick={addParty}
            iconName="Plus"
            iconPosition="left"
          >
            Add First Party
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {parties?.map((party, index) => (
            <div key={party?.id} className="border border-border rounded-lg overflow-hidden">
              {/* Party Header */}
              <div className="flex items-center justify-between p-4 bg-muted/30 border-b border-border">
                <button
                  onClick={() => togglePartyExpansion(party?.id)}
                  className="flex items-center space-x-3 flex-1 text-left"
                >
                  <Icon 
                    name={expandedParty === party?.id ? 'ChevronDown' : 'ChevronRight'} 
                    size={16} 
                  />
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center">
                      <Icon name="User" size={16} className="text-accent" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{getPartyDisplayName(party)}</p>
                      <p className="text-sm text-muted-foreground">
                        {party?.role ? partyRoles?.find(r => r?.value === party?.role)?.label : 'Role not set'}
                      </p>
                    </div>
                  </div>
                </button>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-muted-foreground">Party {index + 1}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeParty(party?.id)}
                    iconName="Trash2"
                  />
                </div>
              </div>

              {/* Party Details */}
              {expandedParty === party?.id && (
                <div className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Role and Entity Type */}
                    <Select
                      label="Party Role"
                      options={partyRoles}
                      value={party?.role}
                      onChange={(value) => updateParty(party?.id, 'role', value)}
                      required
                      placeholder="Select party role"
                    />

                    <Select
                      label="Entity Type"
                      options={entityTypes}
                      value={party?.entityType}
                      onChange={(value) => updateParty(party?.id, 'entityType', value)}
                      required
                    />
                  </div>

                  {/* Company Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      label="Company/Organization Name"
                      type="text"
                      placeholder="Enter company name"
                      value={party?.companyName}
                      onChange={(e) => updateParty(party?.id, 'companyName', e?.target?.value)}
                      required
                    />

                    <Input
                      label="Contact Person"
                      type="text"
                      placeholder="Enter contact person name"
                      value={party?.contactName}
                      onChange={(e) => updateParty(party?.id, 'contactName', e?.target?.value)}
                      required
                    />
                  </div>

                  {/* Contact Details */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Input
                      label="Title/Position"
                      type="text"
                      placeholder="e.g., CEO, Legal Counsel"
                      value={party?.title}
                      onChange={(e) => updateParty(party?.id, 'title', e?.target?.value)}
                    />

                    <Input
                      label="Email Address"
                      type="email"
                      placeholder="contact@company.com"
                      value={party?.email}
                      onChange={(e) => updateParty(party?.id, 'email', e?.target?.value)}
                      required
                    />

                    <Input
                      label="Phone Number"
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={party?.phone}
                      onChange={(e) => updateParty(party?.id, 'phone', e?.target?.value)}
                    />
                  </div>

                  {/* Address */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-foreground">Address Information</h4>
                    <div className="grid grid-cols-1 gap-4">
                      <Input
                        label="Street Address"
                        type="text"
                        placeholder="123 Main Street, Suite 100"
                        value={party?.address?.street || ''}
                        onChange={(e) => updateParty(party?.id, 'address.street', e?.target?.value)}
                      />
                      
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Input
                          label="City"
                          type="text"
                          placeholder="New York"
                          value={party?.address?.city || ''}
                          onChange={(e) => updateParty(party?.id, 'address.city', e?.target?.value)}
                        />

                        <Input
                          label="State/Province"
                          type="text"
                          placeholder="NY"
                          value={party?.address?.state || ''}
                          onChange={(e) => updateParty(party?.id, 'address.state', e?.target?.value)}
                        />

                        <Input
                          label="ZIP/Postal Code"
                          type="text"
                          placeholder="10001"
                          value={party?.address?.zipCode || ''}
                          onChange={(e) => updateParty(party?.id, 'address.zipCode', e?.target?.value)}
                        />

                        <Input
                          label="Country"
                          type="text"
                          placeholder="United States"
                          value={party?.address?.country || 'United States'}
                          onChange={(e) => updateParty(party?.id, 'address.country', e?.target?.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {errors?.parties && (
        <div className="mt-4 p-3 bg-error/10 border border-error/20 rounded-md">
          <p className="text-sm text-error">{errors?.parties}</p>
        </div>
      )}
    </div>
  );
};

export default PartyManagementForm;